import { state } from './state.js';
import * as API from './api.js';
import { clearOnboardingInlineStatus } from './ui/toast.js';
import {
    autoCollapseInputCards,
    openCreateMosaicForEdit,
    revealCreateMosaicMainForOnboarding,
    requestWorkflowScroll,
} from './workflow_selection.js';

const ONBOARDING_VERSION = 8;
const STORAGE_PREFIX = `mosaic_onboarding_v${ONBOARDING_VERSION}`;
const STEP_ORDER = ['main_photo', 'tile_library', 'mosaic_configuration', 'mosaic_ready'];
const STEP_SCROLL_DELAY_MS = 1500;
const STEP_SCROLL_DURATION_MS = 700;
const STEP_DONE_FLASH_MS = 1900;
const STEP_POST_DONE_SCROLL_DELAY_MS = 0;
const INTRO_START_DELAY_MS = 300;
const INTRO_EXPAND_DURATION_MS = 700;
const COACHMARK_SIDE_MARGIN_PX = 12;
const COACHMARK_MAX_WIDTH_PX = 420;
const COACHMARK_SIDE_MIN_WIDTH_PX = 130;
const COACHMARK_SIDE_SWITCH_HYSTERESIS_PX = 14;
const COACHMARK_SIDE_TOP_OFFSET_PX = 150;
const COACHMARK_BOTTOM_GAP_PX = 20;
const COACHMARK_RESERVE_EXTRA_PX = 16;
const COACHMARK_TOAST_CLEARANCE_PX = 12;
const STEP_SCROLL_TOLERANCE_PX = 6;
const STEP_SCROLL_VIEW_MARGIN_PX = 4;
const STEP_SCROLL_GATE_SELECTORS = {
    tile_library: '#tileLibraryCard',
    mosaic_configuration: '#configCard',
};
const SCROLL_BLOCK_KEYS = new Set([
    ' ',
    'Spacebar',
    'PageUp',
    'PageDown',
    'Home',
    'End',
    'ArrowUp',
    'ArrowDown',
]);

const STEP_COPY = {
    main_photo: {
        kicker: 'Welcome!',
        title: 'Step 1 of 3: Main Photo',
        body: 'Upload a photo which will become a mosaic.',
    },
    tile_library_default: {
        kicker: 'Welcome!',
        title: 'Step 2 of 3: Tile Library',
        body: 'Create a Library and upload the photos from which the Mosaic will be made. Or try the Sample Library.',
    },
    tile_library_upload_more: {
        kicker: 'Welcome!',
        title: 'Step 2 of 3: Tile Library',
        body: 'Great, now upload the photos! The more photos a library has, the better the result will be.',
    },
    mosaic_configuration: {
        kicker: 'Welcome!',
        title: 'Step 3 of 3: Mosaic Configuration',
        body: 'Play around with the sliders, and once ready, hit the Generate Mosaic button.',
    },
    mosaic_ready: {
        kicker: 'Welcome',
        title: 'Mosaic is ready!',
        body: 'Great job, you did it! Check out the result and download the Mosaic.',
    },
};

const STEP_CONFIG = {
    main_photo: {
        anchorSelector: '#mainPhotoCard',
        highlightSelector: '#mainPhotoCard',
        fallbackSelector: '#createMosaicCard',
        scrollTarget: 'main',
        prepare() {
            openCreateMosaicForEdit({ focus: false });
        },
        isComplete() {
            return Boolean(state.selectedSourcePhotoId);
        },
    },
    tile_library: {
        anchorSelector: '#tileLibraryCard',
        highlightSelector: '#tileLibraryCard',
        fallbackSelector: '#createMosaicCard',
        scrollTarget: 'library',
        prepare() {
            openCreateMosaicForEdit({ focus: false });
        },
        isComplete(runtime) {
            const lib = _selectedLibrary();
            if (!lib) return false;
            if (!_libraryHasTiles(lib)) {
                runtime.step2NeedsUploadMessage = true;
                return false;
            }
            const libId = String(lib.id || '');
            const requiresUploadComplete =
                Boolean(runtime.step2RequiresUploadCompletion) &&
                Boolean(runtime.createdLibraryId) &&
                runtime.createdLibraryId === libId;
            if (requiresUploadComplete && !runtime.step2UploadCompleted) {
                runtime.step2NeedsUploadMessage = true;
                return false;
            }
            runtime.step2NeedsUploadMessage = false;
            return true;
        },
    },
    mosaic_configuration: {
        anchorSelector: '#configCard',
        highlightSelector: '#configCard',
        fallbackSelector: '#configCard',
        scrollTarget: 'settings',
        prepare() {
            openCreateMosaicForEdit({ focus: false });
        },
        isComplete(runtime) {
            return Boolean(runtime.jobAccepted);
        },
    },
    mosaic_ready: {
        anchorSelector: '#resultCard',
        highlightSelector: '#resultCard',
        fallbackSelector: '#resultCard',
        scrollTarget: 'viewer',
        prepare() {},
        isComplete(runtime) {
            return Boolean(runtime.downloadModalOpened);
        },
    },
};

let _session = null;

function _selectedLibrary() {
    const selectedId = String(state.selectedLibraryId || '').trim();
    if (!selectedId) return null;
    const libs = Array.isArray(state.libraries) ? state.libraries : [];
    return libs.find((lib) => String(lib && lib.id || '') === selectedId) || null;
}

function _libraryHasTiles(lib) {
    if (!lib || typeof lib !== 'object') return false;
    const tileCount = Number(lib.tile_count);
    if (Number.isFinite(tileCount) && tileCount > 0) return true;
    const previews = Array.isArray(lib.previews) ? lib.previews : [];
    return previews.length > 0;
}

function _isVisible(el) {
    if (!el) return false;
    if (el === document.body) return true;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (el.getClientRects && el.getClientRects().length === 0) return false;
    return true;
}

function _isModalOpen() {
    return Boolean(document.querySelector('.modal:not(.hidden)'));
}

function _isEditableTarget(target) {
    if (!target || typeof target.closest !== 'function') return false;
    return Boolean(
        target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""], [contenteditable="plaintext-only"]')
    );
}

function _resolveStepScrollGate() {
    const fallback = {
        allow: false,
        reason: 'inactive',
        stepId: '',
        panelTopPx: null,
        panelBottomPx: null,
        viewTopPx: null,
        viewBottomPx: null,
        viewportHeightPx: null,
    };
    if (!_session || !_session.active || _session.mode !== 'steps') return fallback;
    const stepId = String(_session.currentStepId || '');
    const selector = STEP_SCROLL_GATE_SELECTORS[stepId] || '';
    if (!selector) {
        return { ...fallback, reason: 'not_gated_step', stepId };
    }

    const panel = _resolveHighlight(stepId)
        || _resolveTarget(stepId)
        || document.querySelector(selector);
    if (!panel || !_isVisible(panel)) {
        return { ...fallback, reason: 'panel_missing', stepId };
    }

    const panelRect = panel.getBoundingClientRect();
    const viewportHeightPx = Math.max(0, Math.round(window.innerHeight));
    const viewTopPx = Math.max(0, _spotlightViewportTop() + STEP_SCROLL_VIEW_MARGIN_PX);
    let viewBottomPx = Math.max(0, viewportHeightPx - STEP_SCROLL_VIEW_MARGIN_PX);
    const coachmark = _session.dom && _session.dom.container ? _session.dom.container : null;
    if (
        coachmark &&
        !_session.isTransitioning &&
        !coachmark.classList.contains('hidden') &&
        _session.coachmarkLayoutMode === 'bottom'
    ) {
        const coachmarkRect = coachmark.getBoundingClientRect();
        if (Number.isFinite(coachmarkRect.top)) {
            viewBottomPx = Math.min(viewBottomPx, Math.round(coachmarkRect.top) - STEP_SCROLL_VIEW_MARGIN_PX);
        }
    }
    const panelTopPx = Math.round(panelRect.top);
    const panelBottomPx = Math.round(panelRect.bottom);
    if (viewBottomPx <= viewTopPx) {
        return {
            allow: true,
            reason: 'viewport_occluded',
            stepId,
            panelTopPx,
            panelBottomPx,
            viewTopPx,
            viewBottomPx,
            viewportHeightPx,
        };
    }
    const clippedTop = panelTopPx < (viewTopPx - STEP_SCROLL_TOLERANCE_PX);
    const clippedBottom = panelBottomPx > (viewBottomPx + STEP_SCROLL_TOLERANCE_PX);
    const allow = clippedTop || clippedBottom;
    let reason = 'panel_fits';
    if (clippedTop) reason = 'panel_clipped_top';
    if (clippedBottom) reason = 'panel_clipped_bottom';
    return {
        allow,
        reason,
        stepId,
        panelTopPx,
        panelBottomPx,
        viewTopPx,
        viewBottomPx,
        viewportHeightPx,
    };
}

function _updateStepScrollGate(forceLog = false) {
    if (!_session) return;
    const next = _resolveStepScrollGate();
    const nextAllow = Boolean(next.allow);
    const prevAllow = Boolean(_session.stepScrollAllow);
    const prevReason = String(_session.stepScrollReason || '');
    const prevStep = String(_session.stepScrollStepId || '');
    _session.stepScrollAllow = nextAllow;
    _session.stepScrollReason = String(next.reason || '');
    _session.stepScrollStepId = String(next.stepId || '');
    if (!forceLog && prevAllow === nextAllow && prevStep === _session.stepScrollStepId) return;
    _log('step_scroll_gate', nextAllow ? 'allow' : 'block', {
        step: _session.stepScrollStepId || String(_session.currentStepId || ''),
        reason: _session.stepScrollReason,
        coachmark_mode: String(_session.coachmarkLayoutMode || ''),
        panel_top_px: next.panelTopPx,
        panel_bottom_px: next.panelBottomPx,
        view_top_px: next.viewTopPx,
        view_bottom_px: next.viewBottomPx,
        viewport_h_px: next.viewportHeightPx,
        previous_outcome: prevAllow ? 'allow' : 'block',
        previous_reason: prevReason || '',
        previous_step: prevStep || '',
    });
}

function _shouldBlockPageScroll() {
    if (!_session || !_session.active) return false;
    if (_session.mode !== 'steps') return true;
    if (_session.currentStepId === 'mosaic_ready') return false;
    if (STEP_SCROLL_GATE_SELECTORS[_session.currentStepId]) {
        _updateStepScrollGate(false);
        if (Boolean(_session.stepScrollAllow)) return false;
    }
    return true;
}

function _storageKey(userId) {
    return `${STORAGE_PREFIX}:${String(userId || '').trim()}`;
}

function _defaultStore() {
    const steps = {};
    STEP_ORDER.forEach((id) => {
        steps[id] = false;
    });
    return {
        version: ONBOARDING_VERSION,
        completed: false,
        dismissed: false,
        intro_seen: false,
        started_at: null,
        completed_at: null,
        dismissed_at: null,
        updated_at: null,
        steps,
    };
}

function _readStore(userId) {
    const fallback = _defaultStore();
    if (!userId) return fallback;
    try {
        const raw = localStorage.getItem(_storageKey(userId));
        if (!raw) return fallback;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return fallback;
        if (Number(parsed.version || 0) !== ONBOARDING_VERSION) return fallback;
        const next = _defaultStore();
        next.completed = Boolean(parsed.completed);
        next.dismissed = Boolean(parsed.dismissed);
        next.intro_seen = Boolean(parsed.intro_seen);
        next.started_at = Number(parsed.started_at || 0) || null;
        next.completed_at = Number(parsed.completed_at || 0) || null;
        next.dismissed_at = Number(parsed.dismissed_at || 0) || null;
        next.updated_at = Number(parsed.updated_at || 0) || null;
        next.steps = { ...next.steps, ...(parsed.steps || {}) };
        STEP_ORDER.forEach((id) => {
            next.steps[id] = Boolean(next.steps[id]);
        });
        return next;
    } catch (_e) {
        return fallback;
    }
}

function _writeStore() {
    if (!_session || !_session.userId) return;
    try {
        localStorage.setItem(_storageKey(_session.userId), JSON.stringify(_session.store));
    } catch (_e) {}
}

function _log(action, outcome = 'ok', metadata = {}, durationMs = null) {
    API.logUiFlow('onboarding', action, outcome, metadata || {}, durationMs).catch(() => {});
}

function _disconnectSpotlightObserver() {
    if (!_session) return;
    if (_session.spotlightObserver) {
        try {
            _session.spotlightObserver.disconnect();
        } catch (_e) {}
    }
    _session.spotlightObserver = null;
    _session.spotlightObservedEl = null;
}

function _syncSpotlightTarget(target) {
    if (!_session) return;
    if (target === _session.spotlightObservedEl) return;
    _disconnectSpotlightObserver();
    if (!target || typeof ResizeObserver !== 'function') return;
    try {
        _session.spotlightObserver = new ResizeObserver(() => {
            _requestPosition();
        });
        _session.spotlightObserver.observe(target);
        _session.spotlightObservedEl = target;
    } catch (_e) {
        _disconnectSpotlightObserver();
    }
}

function _teardownDom() {
    if (!_session || !_session.dom) return;
    const { container, spotlight } = _session.dom;
    _disconnectSpotlightObserver();
    if (container && container.parentNode) {
        container.parentNode.removeChild(container);
    }
    if (spotlight && spotlight.parentNode) {
        spotlight.parentNode.removeChild(spotlight);
    }
    _session.dom = null;
    _clearCoachmarkReserve();
    _clearToastBottomOffset();
    document.body.classList.remove('onboarding-bottom-mode');
}

function _cancelPendingTransition() {
    if (!_session) return;
    if (_session.transitionTimer) {
        window.clearTimeout(_session.transitionTimer);
        _session.transitionTimer = 0;
    }
    if (_session.transitionFallbackTimer) {
        window.clearTimeout(_session.transitionFallbackTimer);
        _session.transitionFallbackTimer = 0;
    }
    if (_session.transitionGapTimer) {
        window.clearTimeout(_session.transitionGapTimer);
        _session.transitionGapTimer = 0;
    }
    if (_session.transitionDoneTimer) {
        window.clearTimeout(_session.transitionDoneTimer);
        _session.transitionDoneTimer = 0;
    }
    _session.transitionToken = Number(_session.transitionToken || 0) + 1;
    _session.isTransitioning = false;
    _session.transitionShowingDone = false;
    _session.pendingStepId = '';
    _session.needsRefresh = false;
    _session.transitionDelayDone = false;
    _session.transitionExternalDone = false;
    document.body.classList.remove('onboarding-transitioning');
}

function _hideSpotlight() {
    if (!_session || !_session.dom || !_session.dom.spotlight) return;
    _session.spotlightLastRect = null;
    _session.dom.spotlight.style.top = '';
    _session.dom.spotlight.classList.add('hidden');
    _syncSpotlightTarget(null);
}

function _clearHighlight() {
    if (!_session) return;
    if (_session.highlightedEl) {
        _session.highlightedEl.classList.remove('onboarding-highlight');
        _session.highlightedEl = null;
    }
}

function _unbindAll() {
    if (!_session || !_session.unbinds) return;
    _session.unbinds.forEach((unbind) => {
        try {
            unbind();
        } catch (_e) {}
    });
    _session.unbinds = [];
}

function _stopPolling() {
    // Onboarding progression is event-driven; no background polling.
}

function _cleanupUiState() {
    document.body.classList.remove('onboarding-active');
    document.body.classList.remove('onboarding-intro-mode');
    document.body.classList.remove('onboarding-spotlight-mode');
    document.body.classList.remove('onboarding-bottom-mode');
    document.body.classList.remove('onboarding-transitioning');
    _clearCoachmarkReserve();
    _clearToastBottomOffset();
    _hideSpotlight();
    _clearHighlight();
}

function _dismiss(reason = 'skip') {
    if (!_session || !_session.active) return;
    _session.active = false;
    _session.store.dismissed = true;
    _session.store.dismissed_at = Date.now();
    _session.store.updated_at = Date.now();
    _writeStore();
    _cancelPendingTransition();
    _stopPolling();
    _unbindAll();
    _cleanupUiState();
    _teardownDom();
    _log('dismiss', reason, { reason });
}

function _finishCompleted() {
    if (!_session || !_session.active) return;
    _session.active = false;
    _session.store.completed = true;
    _session.store.completed_at = Date.now();
    _session.store.updated_at = Date.now();
    _writeStore();
    _cancelPendingTransition();
    _stopPolling();
    _unbindAll();
    _cleanupUiState();
    _teardownDom();
    _log('finish', 'ok');
}

function _bind(target, eventName, handler, options = undefined) {
    target.addEventListener(eventName, handler, options);
    _session.unbinds.push(() => target.removeEventListener(eventName, handler, options));
}

function _renderCoachmarkShell() {
    const container = document.createElement('div');
    container.id = 'onboardingCoachmark';
    container.className = 'onboarding-coachmark hidden';
    container.setAttribute('role', 'dialog');
    container.setAttribute('aria-live', 'polite');
    container.innerHTML = `
        <div class="onboarding-kicker" id="onboardingKicker"></div>
        <h3 class="onboarding-title" id="onboardingTitle"></h3>
        <p class="onboarding-body" id="onboardingBody"></p>
        <div class="onboarding-actions">
            <button type="button" class="btn secondary" id="onboardingSkipBtn">Skip tour</button>
            <button type="button" class="btn" id="onboardingNextBtn">Next</button>
        </div>
        <div class="onboarding-inline-status hidden" id="onboardingInlineStatus" role="status" aria-live="polite"></div>
    `;
    document.body.appendChild(container);

    const spotlight = document.createElement('div');
    spotlight.id = 'onboardingSpotlight';
    spotlight.className = 'onboarding-spotlight hidden';
    spotlight.innerHTML = `
        <div class="onboarding-spotlight-cutout" aria-hidden="true"></div>
    `;
    document.body.appendChild(spotlight);

    _session.dom = {
        container,
        spotlight,
        spotlightCutout: spotlight.querySelector('.onboarding-spotlight-cutout'),
        kicker: container.querySelector('#onboardingKicker'),
        title: container.querySelector('#onboardingTitle'),
        body: container.querySelector('#onboardingBody'),
        skipBtn: container.querySelector('#onboardingSkipBtn'),
        nextBtn: container.querySelector('#onboardingNextBtn'),
        inlineStatus: container.querySelector('#onboardingInlineStatus'),
    };
}

function _resolveTarget(stepId) {
    const config = STEP_CONFIG[stepId];
    if (!config) return null;
    const anchor = document.querySelector(config.anchorSelector);
    if (anchor && _isVisible(anchor)) return anchor;
    const fallback = document.querySelector(config.fallbackSelector || '');
    if (fallback && _isVisible(fallback)) return fallback;
    return anchor || fallback || null;
}

function _resolveHighlight(stepId) {
    const config = STEP_CONFIG[stepId];
    if (!config) return null;
    const toCard = (el) => {
        if (!el) return null;
        const card = (typeof el.closest === 'function')
            ? (el.closest('.collapsible-card') || el.closest('.card'))
            : null;
        return card || el;
    };
    const highlight = document.querySelector(config.highlightSelector || '');
    if (highlight && _isVisible(highlight)) return toCard(highlight);
    const fallback = _resolveTarget(stepId);
    return toCard(fallback);
}

function _workflowPanelGapPx() {
    const workflowMain = document.querySelector('.workflow-main');
    const workflowLayout = document.querySelector('.workflow-layout');
    const candidate = workflowMain || workflowLayout;
    if (candidate) {
        const styles = window.getComputedStyle(candidate);
        const parsed = parseFloat(styles.rowGap || styles.gap || '');
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    const rootGap = parseFloat(
        window.getComputedStyle(document.documentElement).getPropertyValue('--panel-gap') || ''
    );
    if (Number.isFinite(rootGap) && rootGap > 0) return rootGap;
    return 20;
}

function _spotlightViewportTop() {
    const header = document.querySelector('.app-header');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    return Math.max(0, Math.round(rect.bottom) - 1);
}

function _setCoachmarkReserve(valuePx = 0) {
    const root = document.documentElement;
    if (!root) return;
    const reservePx = Math.max(0, Math.round(Number(valuePx || 0)));
    root.style.setProperty('--onboarding-coachmark-reserve', `${reservePx}px`);
}

function _clearCoachmarkReserve() {
    const root = document.documentElement;
    if (!root) return;
    root.style.removeProperty('--onboarding-coachmark-reserve');
}

function _setToastBottomOffset(valuePx = 0) {
    const root = document.documentElement;
    if (!root) return;
    const offsetPx = Math.max(0, Math.round(Number(valuePx || 0)));
    root.style.setProperty('--toast-bottom-offset', `${offsetPx}px`);
}

function _clearToastBottomOffset() {
    const root = document.documentElement;
    if (!root) return;
    root.style.removeProperty('--toast-bottom-offset');
}

function _resolveMainContentRight(viewportWidth) {
    const candidates = [
        '.container',
        '.workflow-layout',
        '.workflow-main',
        '.main-content',
    ];
    for (const selector of candidates) {
        const el = document.querySelector(selector);
        if (!el || typeof el.getBoundingClientRect !== 'function') continue;
        const rect = el.getBoundingClientRect();
        if (!rect || !Number.isFinite(rect.right)) continue;
        return Math.max(0, Math.min(Math.round(viewportWidth), Math.round(rect.right)));
    }
    return Math.round(viewportWidth * 0.85);
}

function _shouldUseSideLayout(sideWidthPx) {
    const width = Math.max(0, Number(sideWidthPx || 0));
    const previousMode = String(_session && _session.coachmarkLayoutMode || '');
    const enterThreshold = COACHMARK_SIDE_MIN_WIDTH_PX + COACHMARK_SIDE_SWITCH_HYSTERESIS_PX;
    const leaveThreshold = COACHMARK_SIDE_MIN_WIDTH_PX - COACHMARK_SIDE_SWITCH_HYSTERESIS_PX;
    if (previousMode === 'side') return width >= leaveThreshold;
    if (previousMode === 'bottom') return width >= enterThreshold;
    return width >= COACHMARK_SIDE_MIN_WIDTH_PX;
}

function _applyCoachmarkLayoutMode(mode, metadata = {}) {
    if (!_session) return;
    const nextMode = mode === 'side' ? 'side' : 'bottom';
    const previousMode = String(_session.coachmarkLayoutMode || '');
    _session.coachmarkLayoutMode = nextMode;
    document.body.classList.toggle('onboarding-bottom-mode', nextMode === 'bottom' && _session.mode === 'steps');
    if (previousMode !== nextMode) {
        _log('coachmark_layout_mode', nextMode, {
            mode: nextMode,
            previous_mode: previousMode || 'none',
            ...metadata,
        });
    }
}

function _positionCoachmark() {
    if (!_session || !_session.dom) return;
    const { container } = _session.dom;
    if (!container) return;

    if (_session.isTransitioning && !_session.transitionShowingDone) {
        container.classList.add('hidden');
        _clearCoachmarkReserve();
        _clearToastBottomOffset();
        document.body.classList.remove('onboarding-bottom-mode');
        return;
    }

    if (_session.mode === 'intro') {
        _session.coachmarkLayoutMode = '';
        document.body.classList.remove('onboarding-bottom-mode');
        _clearToastBottomOffset();
        _clearCoachmarkReserve();
        const viewportWidth = Math.max(0, Math.round(window.innerWidth));
        const viewportHeight = Math.max(0, Math.round(window.innerHeight));
        const availableWidth = Math.max(0, viewportWidth - (COACHMARK_SIDE_MARGIN_PX * 2));
        const width = Math.min(COACHMARK_MAX_WIDTH_PX, availableWidth);
        container.style.width = `${width}px`;
        container.style.maxHeight = `${Math.max(180, viewportHeight - 24)}px`;
        container.classList.remove('hidden');
        container.style.right = 'auto';
        container.style.bottom = 'auto';
        const cardRect = container.getBoundingClientRect();
        const centeredLeft = Math.round((viewportWidth - cardRect.width) * 0.5);
        const centeredTop = Math.round((viewportHeight - cardRect.height) * 0.35);
        const maxLeft = Math.max(COACHMARK_SIDE_MARGIN_PX, viewportWidth - cardRect.width - COACHMARK_SIDE_MARGIN_PX);
        const maxTop = Math.max(12, viewportHeight - cardRect.height - 12);
        const left = Math.max(COACHMARK_SIDE_MARGIN_PX, Math.min(centeredLeft, maxLeft));
        const top = Math.max(12, Math.min(centeredTop, maxTop));
        container.style.left = `${left}px`;
        container.style.top = `${top}px`;
        return;
    }

    const viewportWidth = Math.max(0, Math.round(window.innerWidth));
    const viewportHeight = Math.max(0, Math.round(window.innerHeight));
    const availableWidth = Math.max(0, viewportWidth - (COACHMARK_SIDE_MARGIN_PX * 2));
    const contentRight = _resolveMainContentRight(viewportWidth);
    const laneStartPx = Math.max(0, Math.round(contentRight));
    const laneWidthPx = Math.max(0, viewportWidth - laneStartPx);
    const sideCandidateWidthPx = Math.max(0, Math.min(
        COACHMARK_MAX_WIDTH_PX,
        availableWidth,
        laneWidthPx - (COACHMARK_SIDE_MARGIN_PX * 2)
    ));
    const useSideLayout = _shouldUseSideLayout(sideCandidateWidthPx);
    container.classList.remove('hidden');
    if (useSideLayout) {
        const sideTop = Math.max(12, _spotlightViewportTop() + COACHMARK_SIDE_TOP_OFFSET_PX);
        const sideWidth = Math.max(0, Math.min(sideCandidateWidthPx, availableWidth));
        const centeredInLaneLeft = Math.round(laneStartPx + ((laneWidthPx - sideWidth) * 0.5));
        const maxSideLeft = Math.max(COACHMARK_SIDE_MARGIN_PX, viewportWidth - sideWidth - COACHMARK_SIDE_MARGIN_PX);
        const sideLeft = Math.max(COACHMARK_SIDE_MARGIN_PX, Math.min(centeredInLaneLeft, maxSideLeft));
        container.style.width = `${Math.round(sideWidth)}px`;
        container.style.maxHeight = `${Math.max(160, viewportHeight - sideTop - 12)}px`;
        container.style.left = `${sideLeft}px`;
        container.style.top = `${Math.round(sideTop)}px`;
        container.style.right = 'auto';
        container.style.bottom = 'auto';
        _clearCoachmarkReserve();
        _clearToastBottomOffset();
        _applyCoachmarkLayoutMode('side', {
            viewport_w: viewportWidth,
            viewport_h: viewportHeight,
            content_right_px: contentRight,
            lane_width_px: laneWidthPx,
            coachmark_w_px: Math.round(sideWidth),
            coachmark_x_px: Math.round(sideLeft),
        });
        return;
    }

    const width = Math.min(COACHMARK_MAX_WIDTH_PX, availableWidth);
    container.style.width = `${width}px`;
    container.style.maxHeight = `${Math.max(160, viewportHeight - 24)}px`;
    container.style.top = 'auto';
    container.style.right = 'auto';
    container.style.bottom = `max(${COACHMARK_BOTTOM_GAP_PX}px, env(safe-area-inset-bottom))`;
    const cardRect = container.getBoundingClientRect();
    const centeredLeft = Math.round((viewportWidth - cardRect.width) * 0.5);
    const maxLeft = Math.max(COACHMARK_SIDE_MARGIN_PX, viewportWidth - cardRect.width - COACHMARK_SIDE_MARGIN_PX);
    const clampedLeft = Math.max(COACHMARK_SIDE_MARGIN_PX, Math.min(centeredLeft, maxLeft));
    container.style.left = `${Math.round(clampedLeft)}px`;
    const resolvedRect = container.getBoundingClientRect();
    const computedBottomPx = parseFloat(window.getComputedStyle(container).bottom || '');
    const bottomOffsetPx = Number.isFinite(computedBottomPx) ? computedBottomPx : COACHMARK_BOTTOM_GAP_PX;
    const reservePx = Math.round(resolvedRect.height + bottomOffsetPx + COACHMARK_RESERVE_EXTRA_PX);
    _setCoachmarkReserve(reservePx);
    _setToastBottomOffset(reservePx + COACHMARK_TOAST_CLEARANCE_PX);
    _applyCoachmarkLayoutMode('bottom', {
        viewport_w: viewportWidth,
        viewport_h: viewportHeight,
        content_right_px: contentRight,
        lane_width_px: laneWidthPx,
        coachmark_w_px: Math.round(resolvedRect.width),
        reserve_px: reservePx,
    });
}

function _positionSpotlight() {
    if (!_session || !_session.dom || !_session.dom.spotlight) return;
    const spotlight = _session.dom.spotlight;
    if (
        _session.mode !== 'steps' ||
        (_session.isTransitioning && !_session.transitionShowingDone) ||
        !_session.currentStepId ||
        _session.currentStepId === 'mosaic_ready'
    ) {
        spotlight.classList.add('hidden');
        _syncSpotlightTarget(null);
        return;
    }
    const target = _resolveHighlight(_session.currentStepId);
    if (!target) {
        spotlight.classList.add('hidden');
        _syncSpotlightTarget(null);
        return;
    }
    const cutout = _session.dom.spotlightCutout;
    if (!cutout) {
        spotlight.classList.add('hidden');
        _syncSpotlightTarget(null);
        return;
    }

    _syncSpotlightTarget(target);
    const rect = target.getBoundingClientRect();
    const viewportW = Math.max(0, Math.round(window.innerWidth));
    const viewportH = Math.max(0, Math.round(window.innerHeight));
    const overlayTop = _spotlightViewportTop();
    const overlayHeight = Math.max(0, viewportH - overlayTop);
    if (!viewportW || !overlayHeight) {
        spotlight.classList.add('hidden');
        return;
    }
    spotlight.style.top = `${overlayTop}px`;
    const visibleLeft = Math.max(0, Math.round(rect.left));
    const visibleRight = Math.min(viewportW, Math.round(rect.right));
    const visibleTopAbs = Math.max(overlayTop, Math.round(rect.top));
    const visibleBottomAbs = Math.min(viewportH, Math.round(rect.bottom));
    const visibleWidth = Math.max(0, visibleRight - visibleLeft);
    const visibleHeight = Math.max(0, visibleBottomAbs - visibleTopAbs);

    let left = 0;
    let top = 0;
    let width = 0;
    let height = 0;
    if (visibleWidth > 0 && visibleHeight > 0) {
        left = visibleLeft;
        top = visibleTopAbs - overlayTop;
        width = visibleWidth;
        height = visibleHeight;
        _session.spotlightLastRect = { left, top, width, height };
    } else if (_session.currentStepId === 'tile_library' && _session.spotlightLastRect) {
        ({ left, top, width, height } = _session.spotlightLastRect);
    } else {
        _session.spotlightLastRect = null;
        spotlight.classList.add('hidden');
        return;
    }

    const computed = window.getComputedStyle(target);
    cutout.style.left = `${left}px`;
    cutout.style.top = `${top}px`;
    cutout.style.width = `${width}px`;
    cutout.style.height = `${height}px`;
    cutout.style.borderRadius = computed.borderRadius || '12px';

    spotlight.classList.remove('hidden');
}

function _requestPosition() {
    if (!_session) return;
    if (_session.positionRaf) {
        window.cancelAnimationFrame(_session.positionRaf);
    }
    _session.positionRaf = window.requestAnimationFrame(() => {
        _session.positionRaf = 0;
        _positionCoachmark();
        _positionSpotlight();
        _updateStepScrollGate(false);
    });
}

function _firstIncompleteStep() {
    for (const id of STEP_ORDER) {
        if (!_session.store.steps[id]) return id;
    }
    return '';
}

function _markStepComplete(stepId, source = 'auto') {
    if (!_session || !stepId || !_session.store || _session.store.steps[stepId]) return false;
    const now = Date.now();
    _session.store.steps[stepId] = true;
    _session.store.updated_at = now;
    _writeStore();
    const durationMs = _session.currentStepId === stepId && _session.stepShownAtMs
        ? Math.max(0, now - _session.stepShownAtMs)
        : null;
    _log('complete', stepId, { step: stepId, source }, durationMs);
    return true;
}

function _evaluateAutoCompletion() {
    if (!_session || !_session.active || _session.mode !== 'steps') return false;
    const stepId = _session.currentStepId || _firstIncompleteStep();
    if (!stepId || _session.store.steps[stepId]) return false;
    const cfg = STEP_CONFIG[stepId];
    if (!cfg || typeof cfg.isComplete !== 'function') return false;
    if (!cfg.isComplete(_session.runtime)) return false;
    return _markStepComplete(stepId, 'auto');
}

function _setCoachmarkView({
    kicker = '',
    title = '',
    body = '',
    skipLabel = 'Skip tour',
    showSkip = true,
    showNext = false,
    nextLabel = 'Next',
    nextDisabled = false,
    done = false,
    intro = false,
}) {
    if (!_session || !_session.dom) return;
    _session.dom.container.classList.toggle('is-done', Boolean(done));
    _session.dom.container.classList.toggle('is-intro', Boolean(intro));
    _session.dom.kicker.textContent = kicker;
    _session.dom.title.textContent = title;
    _session.dom.body.textContent = body;
    _session.dom.skipBtn.classList.toggle('hidden', !showSkip);
    _session.dom.skipBtn.textContent = skipLabel;
    _session.dom.nextBtn.classList.toggle('hidden', !showNext);
    _session.dom.nextBtn.textContent = nextLabel;
    _session.dom.nextBtn.disabled = Boolean(nextDisabled);
}

function _showIntro() {
    if (!_session || !_session.active) return;
    _session.mode = 'intro';
    _session.currentStepId = '';
    _session.stepShownAtMs = Date.now();
    _session.isTransitioning = false;
    _session.pendingStepId = '';
    _session.needsRefresh = false;
    _clearHighlight();
    _hideSpotlight();
    document.body.classList.add('onboarding-active');
    document.body.classList.add('onboarding-intro-mode');
    document.body.classList.remove('onboarding-spotlight-mode');
    document.body.classList.remove('onboarding-transitioning');
    clearOnboardingInlineStatus();
    autoCollapseInputCards({
        animate: false,
        scroll: false,
        openViewer: false,
        collapseOrder: ['main', 'library', 'settings', 'create', 'history', 'viewer'],
    }).catch(() => {});
    requestWorkflowScroll('history', {
        source: 'onboarding_intro',
        behavior: 'auto',
        pushHistory: false,
    });
    _setCoachmarkView({
        kicker: 'Welcome to mosaic4me',
        title: 'Ready to make the first artwork?',
        body: '',
        showSkip: false,
        showNext: true,
        nextLabel: "Let's go!",
        nextDisabled: false,
        intro: true,
    });
    if (_session.dom && _session.dom.container) {
        _session.dom.container.classList.remove('hidden');
    }
    _positionCoachmark();
    _requestPosition();
    _log('intro_view', 'ok');
}

function _stepCopy(stepId) {
    if (stepId === 'tile_library' && _session.runtime.step2NeedsUploadMessage) {
        return STEP_COPY.tile_library_upload_more;
    }
    if (stepId === 'tile_library') {
        return STEP_COPY.tile_library_default;
    }
    return STEP_COPY[stepId] || { kicker: 'Welcome!', title: '', body: '' };
}

function _showStepDone(stepId) {
    const copy = _stepCopy(stepId);
    _setCoachmarkView({
        kicker: 'Done!',
        title: copy.title,
        body: copy.body,
        showSkip: false,
        showNext: false,
        done: true,
    });
    _requestPosition();
}

function _prepareStep(stepId, options = {}) {
    const cfg = STEP_CONFIG[stepId];
    if (!cfg) return;
    const skipScroll = Boolean(options.skipScroll);
    if (_isModalOpen()) {
        _log('focus_guard_blocked', stepId, { step: stepId });
        return;
    }
    if (typeof cfg.prepare === 'function') {
        try {
            cfg.prepare();
        } catch (_e) {}
    }
    if (!skipScroll && cfg.scrollTarget) {
        _log('step_transition_scheduled', 'ok', {
            step: stepId,
            target: cfg.scrollTarget,
            delay_ms: STEP_SCROLL_DELAY_MS,
            scroll_ms: STEP_SCROLL_DURATION_MS,
        });
        requestWorkflowScroll(cfg.scrollTarget, {
            source: 'onboarding',
            afterFrame: true,
            delayMs: STEP_SCROLL_DELAY_MS,
            durationMs: STEP_SCROLL_DURATION_MS,
        });
    }
}

function _resolveScrollTarget(stepId) {
    const cfg = STEP_CONFIG[stepId];
    if (cfg && cfg.scrollTarget) return cfg.scrollTarget;
    return _resolveTarget(stepId);
}

function _maybeCommitGenerateTransition(token, fromStep, toStep) {
    if (!_session || !_session.active || !_session.isTransitioning) return;
    if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
    if (_session.pendingStepId !== toStep) return;
    if (!Boolean(_session.runtime && _session.runtime.mosaicReady)) return;
    if (!_session.transitionDelayDone || !_session.transitionExternalDone) return;
    _commitStepTransition(token, fromStep, toStep);
}

function _commitStepTransition(token, fromStep, toStep) {
    if (!_session || !_session.active) return;
    if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
    _session.isTransitioning = false;
    _session.transitionShowingDone = false;
    _session.pendingStepId = '';
    _session.transitionTimer = 0;
    if (_session.transitionDoneTimer) {
        window.clearTimeout(_session.transitionDoneTimer);
        _session.transitionDoneTimer = 0;
    }
    if (_session.transitionFallbackTimer) {
        window.clearTimeout(_session.transitionFallbackTimer);
        _session.transitionFallbackTimer = 0;
    }
    _session.transitionDelayDone = false;
    _session.transitionExternalDone = false;
    document.body.classList.remove('onboarding-transitioning');
    _session.currentStepId = toStep;
    _session.stepShownAtMs = Date.now();
    _prepareStep(toStep, { skipScroll: true });
    _log('transition_committed', 'ok', {
        from_step: fromStep,
        to_step: toStep,
    });
    _showCurrentStep();
    if (_session.needsRefresh) {
        _session.needsRefresh = false;
        _showCurrentStep();
    }
}

function _beginStepTransition(fromStep, toStep, trigger = 'auto') {
    if (!_session || !_session.active || _session.isTransitioning) return;
    _cancelPendingTransition();
    _session.isTransitioning = true;
    _session.transitionShowingDone = false;
    _session.pendingStepId = toStep;
    _session.needsRefresh = false;
    _session.transitionDelayDone = false;
    _session.transitionExternalDone = false;
    _session.transitionToken = Number(_session.transitionToken || 0) + 1;
    const token = _session.transitionToken;
    _clearHighlight();
    clearOnboardingInlineStatus();
    document.body.classList.remove('onboarding-intro-mode');
    document.body.classList.add('onboarding-active');
    document.body.classList.add('onboarding-transitioning');

    _log('transition_queued', 'ok', {
        from_step: fromStep,
        to_step: toStep,
        trigger,
        delay_ms: STEP_SCROLL_DELAY_MS,
        scroll_ms: STEP_SCROLL_DURATION_MS,
    });

    const scheduleTransition = (delayMs = STEP_SCROLL_DELAY_MS) => {
        if (!_session || !_session.active) return;
        if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
        _session.transitionShowingDone = false;
        if (_session.dom && _session.dom.container) {
            _session.dom.container.classList.add('hidden');
        }
        _requestPosition();

        if (toStep === 'mosaic_ready') {
            _session.transitionExternalDone = Boolean(
                _session.runtime.mosaicReady && _session.runtime.jobCompleteScrollDone
            );
            _session.transitionTimer = window.setTimeout(() => {
                _session.transitionTimer = 0;
                if (!_session || !_session.active) return;
                if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
                _session.transitionDelayDone = true;
                _maybeCommitGenerateTransition(token, fromStep, toStep);
            }, delayMs);
            _session.transitionFallbackTimer = window.setTimeout(() => {
                _session.transitionFallbackTimer = 0;
                if (!_session || !_session.active || !_session.isTransitioning) return;
                if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
                if (!_session.runtime.mosaicReady) return;
                _session.transitionExternalDone = true;
                _log('transition_scroll_fallback', 'timeout', {
                    from_step: fromStep,
                    to_step: toStep,
                });
                _maybeCommitGenerateTransition(token, fromStep, toStep);
            }, delayMs + 7000);
            return;
        }

        _session.transitionTimer = window.setTimeout(() => {
            _session.transitionTimer = 0;
            if (!_session || !_session.active) return;
            if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
            const scrollTarget = _resolveScrollTarget(toStep);
            if (!scrollTarget) {
                _commitStepTransition(token, fromStep, toStep);
                return;
            }
            _log('transition_scroll_start', 'ok', {
                from_step: fromStep,
                to_step: toStep,
            });
            requestWorkflowScroll(scrollTarget, {
                source: 'onboarding_transition',
                afterFrame: true,
                durationMs: STEP_SCROLL_DURATION_MS,
                onDone: () => {
                    _log('transition_scroll_end', 'ok', {
                        from_step: fromStep,
                        to_step: toStep,
                    });
                    _commitStepTransition(token, fromStep, toStep);
                },
            });
        }, delayMs);
    };

    const canShowDone =
        Boolean(fromStep) &&
        Boolean(_session.dom && _session.dom.container) &&
        _session.mode === 'steps';
    if (!canShowDone) {
        scheduleTransition();
        return;
    }
    _session.transitionShowingDone = true;
    _session.dom.container.classList.remove('hidden');
    _showStepDone(fromStep);
    _session.transitionDoneTimer = window.setTimeout(() => {
        _session.transitionDoneTimer = 0;
        if (!_session || !_session.active) return;
        if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
        _session.transitionShowingDone = false;
        if (_session.dom && _session.dom.container) {
            _session.dom.container.classList.add('hidden');
        }
        _requestPosition();
        if (STEP_POST_DONE_SCROLL_DELAY_MS <= 0) {
            scheduleTransition(0);
            return;
        }
        _session.transitionGapTimer = window.setTimeout(() => {
            _session.transitionGapTimer = 0;
            if (!_session || !_session.active) return;
            if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
            scheduleTransition(0);
        }, STEP_POST_DONE_SCROLL_DELAY_MS);
    }, STEP_DONE_FLASH_MS);
}

function _showCurrentStep() {
    if (!_session || !_session.active || _session.mode !== 'steps') return;
    if (_session.isTransitioning) {
        _session.needsRefresh = true;
        return;
    }
    _evaluateAutoCompletion();
    const nextStep = _firstIncompleteStep();
    if (!nextStep) {
        _finishCompleted();
        return;
    }

    if (_session.currentStepId && _session.currentStepId !== nextStep) {
        _beginStepTransition(_session.currentStepId, nextStep, 'auto');
        return;
    }

    const previousStep = _session.currentStepId;
    const stepChanged = previousStep !== nextStep;
    if (stepChanged) {
        _session.currentStepId = nextStep;
        _session.stepShownAtMs = Date.now();
        _prepareStep(nextStep, { skipScroll: true });
        clearOnboardingInlineStatus();
    }

    const copy = _stepCopy(nextStep);
    const waitingUpload =
        nextStep === 'tile_library' &&
        Boolean(_session.runtime.step2NeedsUploadMessage) &&
        Boolean(_session.runtime.step2RequiresUploadCompletion);
    _setCoachmarkView({
        kicker: copy.kicker,
        title: copy.title,
        body: copy.body,
        skipLabel: nextStep === 'mosaic_ready' ? 'Close' : 'Skip tour',
        showNext: false,
    });

    if (stepChanged) {
        _clearHighlight();
        const highlight = _resolveHighlight(nextStep);
        if (highlight) {
            highlight.classList.add('onboarding-highlight');
            _session.highlightedEl = highlight;
        }
    }

    document.body.classList.add('onboarding-active');
    document.body.classList.remove('onboarding-intro-mode');
    document.body.classList.toggle('onboarding-spotlight-mode', nextStep !== 'mosaic_ready');
    _updateStepScrollGate(stepChanged && Boolean(STEP_SCROLL_GATE_SELECTORS[nextStep]));
    _requestPosition();

    const viewKey = `${nextStep}:${waitingUpload ? 'wait_upload' : 'normal'}`;
    if (_session.lastViewKey !== viewKey || stepChanged) {
        _session.lastViewKey = viewKey;
        _log('step_view', nextStep, {
            step: nextStep,
            waiting_upload: waitingUpload,
            step_changed: stepChanged,
        });
    }
}

function _reopenConfigurationStep(reason = 'job_interrupted') {
    if (!_session || !_session.active || _session.mode !== 'steps') return;
    const wasPendingReady = _session.pendingStepId === 'mosaic_ready';
    const wasStep4 = _session.currentStepId === 'mosaic_ready';
    if (!wasPendingReady && !wasStep4) return;
    _cancelPendingTransition();
    _session.runtime.jobAccepted = false;
    _session.runtime.mosaicReady = false;
    _session.runtime.jobCompleteScrollDone = false;
    _session.store.steps.mosaic_configuration = false;
    _session.store.steps.mosaic_ready = false;
    _session.store.updated_at = Date.now();
    _writeStore();
    _session.currentStepId = 'mosaic_configuration';
    _session.stepShownAtMs = Date.now();
    _log('step_reopen', reason, {
        step: 'mosaic_configuration',
        reason,
    });
    _showCurrentStep();
}

function _startSteps() {
    if (!_session || !_session.mode || !_session.active) return;
    _session.mode = 'steps';
    _session.store.intro_seen = true;
    _session.store.updated_at = Date.now();
    _writeStore();
    _log('intro_next', 'ok');
    _cancelPendingTransition();
    _session.isTransitioning = true;
    _session.transitionShowingDone = false;
    _session.pendingStepId = 'main_photo';
    _session.needsRefresh = false;
    _session.transitionToken = Number(_session.transitionToken || 0) + 1;
    const token = _session.transitionToken;
    if (_session.dom && _session.dom.container) {
        _session.dom.container.classList.add('hidden');
    }
    _clearHighlight();
    _hideSpotlight();
    document.body.classList.remove('onboarding-intro-mode');
    document.body.classList.remove('onboarding-spotlight-mode');
    document.body.classList.add('onboarding-active');
    document.body.classList.add('onboarding-transitioning');
    _requestPosition();

    _session.transitionTimer = window.setTimeout(() => {
        _session.transitionTimer = 0;
        if (!_session || !_session.active) return;
        if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
        Promise.resolve(revealCreateMosaicMainForOnboarding({
            animate: true,
            durationMs: INTRO_EXPAND_DURATION_MS,
            focus: false,
        }))
            .catch(() => {})
            .finally(() => {
                if (!_session || !_session.active) return;
                if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
                requestWorkflowScroll('main', {
                    source: 'card_click',
                    afterFrame: true,
                    durationMs: STEP_SCROLL_DURATION_MS,
                    onDone: () => {
                        if (!_session || !_session.active) return;
                        if (Number(_session.transitionToken || 0) !== Number(token || 0)) return;
                        _session.isTransitioning = false;
                        _session.pendingStepId = '';
                        _session.transitionDelayDone = false;
                        _session.transitionExternalDone = false;
                        document.body.classList.remove('onboarding-transitioning');
                        _showCurrentStep();
                        if (_session.needsRefresh) {
                            _session.needsRefresh = false;
                            _showCurrentStep();
                        }
                    },
                });
            });
    }, INTRO_START_DELAY_MS);
}

function _onNext() {
    if (!_session || !_session.active) return;
    if (_session.mode === 'intro') {
        _startSteps();
    }
}

function _onSkip() {
    if (!_session || !_session.active) return;
    const reason =
        _session.mode === 'steps' && _session.currentStepId === 'mosaic_ready'
            ? 'close_button'
            : 'skip_button';
    _dismiss(reason);
}

function _bindCoachmarkUi() {
    if (!_session || !_session.dom) return;
    const { nextBtn, skipBtn } = _session.dom;
    _bind(nextBtn, 'click', () => _onNext());
    _bind(skipBtn, 'click', () => _onSkip());
}

function _bindLifecycle() {
    _bind(window, 'resize', () => _requestPosition());
    _bind(window, 'scroll', () => _requestPosition(), true);
    _bind(window, 'wheel', (event) => {
        if (!_session || !_session.active) return;
        if (_isModalOpen()) return;
        if (!_shouldBlockPageScroll()) return;
        event.preventDefault();
    }, { passive: false });
    _bind(window, 'touchmove', (event) => {
        if (!_session || !_session.active) return;
        if (_isModalOpen()) return;
        if (!_shouldBlockPageScroll()) return;
        event.preventDefault();
    }, { passive: false });
    _bind(document, 'keydown', (event) => {
        if (event.key === 'Escape') _dismiss('escape_key');
        if (!_session || !_session.active) return;
        if (_isModalOpen()) return;
        if (!_shouldBlockPageScroll()) return;
        if (!SCROLL_BLOCK_KEYS.has(String(event.key || ''))) return;
        if (_isEditableTarget(event.target)) return;
        event.preventDefault();
    });

    _bind(document, 'library:created', (event) => {
        const created = event && event.detail && event.detail.library ? event.detail.library : null;
        const createdId = String(created && created.id || '');
        const selected = _selectedLibrary();
        const selectedId = selected ? String(selected.id || '') : '';
        _session.runtime.createdLibraryId = createdId || selectedId || _session.runtime.createdLibraryId || '';
        _session.runtime.step2RequiresUploadCompletion = true;
        _session.runtime.step2UploadCompleted = false;
        _session.runtime.step2NeedsUploadMessage = true;
        _log('step2_wait_upload', 'ok', {
            step: 'tile_library',
            created_library_id: _session.runtime.createdLibraryId || '',
        });
        if (_session.mode === 'steps' && _session.currentStepId === 'tile_library') {
            _showCurrentStep();
        }
    });

    _bind(document, 'library:selected-updated', (event) => {
        const detailLib = event && event.detail && event.detail.library ? event.detail.library : null;
        const selected = detailLib || _selectedLibrary();
        const selectedId = selected ? String(selected.id || '') : '';
        const createdId = String(_session.runtime.createdLibraryId || '');
        if (selectedId && createdId && selectedId !== createdId) {
            _session.runtime.step2RequiresUploadCompletion = false;
            _session.runtime.step2UploadCompleted = false;
            _session.runtime.step2NeedsUploadMessage = false;
        }
        if (selectedId && createdId && selectedId === createdId) {
            _session.runtime.step2RequiresUploadCompletion = true;
        }
        _showCurrentStep();
    });
    _bind(document, 'source:uploaded', () => {
        _showCurrentStep();
    });
    _bind(document, 'library:upload:complete', (event) => {
        const detail = event && event.detail ? event.detail : {};
        const selected = _selectedLibrary();
        const selectedId = selected ? String(selected.id || '') : '';
        const libId = String(detail.library_id || '');
        const createdId = String(_session.runtime.createdLibraryId || '');
        if (!createdId || !libId || libId !== createdId || selectedId !== createdId) return;
        _session.runtime.step2UploadCompleted = true;
        _session.runtime.step2NeedsUploadMessage = false;
        _log('step2_upload_complete', 'ok', {
            step: 'tile_library',
            library_id: libId,
            added: Number(detail.added || 0),
        });
        _showCurrentStep();
    });
    _bind(document, 'job:accepted', () => {
        _session.runtime.jobAccepted = true;
        _session.runtime.mosaicReady = false;
        _session.runtime.jobCompleteScrollDone = false;
        _showCurrentStep();
    });
    _bind(document, 'job:complete', () => {
        _session.runtime.mosaicReady = true;
        if (_session.isTransitioning && _session.pendingStepId === 'mosaic_ready') {
            _maybeCommitGenerateTransition(_session.transitionToken, 'mosaic_configuration', 'mosaic_ready');
        }
        _showCurrentStep();
    });
    _bind(document, 'job:error', () => {
        _reopenConfigurationStep('job_error');
    });
    _bind(document, 'job:cancelled', () => {
        _reopenConfigurationStep('job_cancelled');
    });
    _bind(document, 'workflow:sequence', (event) => {
        const detail = event && event.detail ? event.detail : {};
        const phase = String(detail.phase || '');
        const source = String(detail.source || '');
        if (source !== 'job_complete') return;
        if (phase === 'scroll_start') {
            _session.runtime.jobCompleteScrollDone = false;
            return;
        }
        if (phase !== 'scroll_end') return;
        _session.runtime.jobCompleteScrollDone = true;
        if (_session.isTransitioning && _session.pendingStepId === 'mosaic_ready') {
            _session.transitionExternalDone = true;
            _maybeCommitGenerateTransition(_session.transitionToken, 'mosaic_configuration', 'mosaic_ready');
        }
    });
    _bind(document, 'onboarding:download_modal_open', () => {
        _session.runtime.downloadModalOpened = true;
        _showCurrentStep();
    });
}

function _shouldSkipForExistingUser(historyCount) {
    return Number(historyCount || 0) > 0;
}

function _markCompletedSilently() {
    if (!_session) return;
    _session.store.intro_seen = true;
    STEP_ORDER.forEach((stepId) => {
        _session.store.steps[stepId] = true;
    });
    _session.store.completed = true;
    _session.store.completed_at = Date.now();
    _session.store.updated_at = Date.now();
    _writeStore();
}

export function isOnboardingMosaicReadyContext() {
    if (!_session || !_session.active || _session.mode !== 'steps') return false;
    const currentStep = String(_session.currentStepId || '');
    const pendingStep = String(_session.pendingStepId || '');
    return currentStep === 'mosaic_ready' || pendingStep === 'mosaic_ready';
}

export function initGuidedOnboarding(options = {}) {
    if (_session && _session.initialized) return;
    const userId = String(state.userId || '').trim();
    if (!userId || state.isAnonymous) return;
    if (options && options.resumed) return;
    if (options && options.handledBillingReturn) return;

    _session = {
        initialized: true,
        active: false,
        mode: 'intro',
        userId,
        store: _readStore(userId),
        runtime: {
            jobAccepted: false,
            downloadModalOpened: false,
            step2NeedsUploadMessage: false,
            step2RequiresUploadCompletion: false,
            step2UploadCompleted: false,
            createdLibraryId: '',
            mosaicReady: false,
            jobCompleteScrollDone: false,
        },
        dom: null,
        currentStepId: '',
        stepShownAtMs: 0,
        highlightedEl: null,
        isTransitioning: false,
        transitionShowingDone: false,
        pendingStepId: '',
        needsRefresh: false,
        transitionDoneTimer: 0,
        transitionGapTimer: 0,
        transitionTimer: 0,
        transitionFallbackTimer: 0,
        transitionToken: 0,
        transitionDelayDone: false,
        transitionExternalDone: false,
        lastViewKey: '',
        unbinds: [],
        positionRaf: 0,
        spotlightObserver: null,
        spotlightObservedEl: null,
        spotlightLastRect: null,
        coachmarkLayoutMode: '',
        stepScrollAllow: false,
        stepScrollReason: 'inactive',
        stepScrollStepId: '',
    };

    if (_session.store.steps.mosaic_configuration && !_session.store.steps.mosaic_ready) {
        _session.store.steps.mosaic_configuration = false;
        _session.store.updated_at = Date.now();
        _writeStore();
    }

    if (_shouldSkipForExistingUser(options.historyCount || 0)) {
        _markCompletedSilently();
        return;
    }
    if (_session.store.completed || _session.store.dismissed) {
        return;
    }

    if (!_session.store.started_at) {
        _session.store.started_at = Date.now();
        _session.store.updated_at = _session.store.started_at;
        _writeStore();
    }

    _session.active = true;
    _renderCoachmarkShell();
    _bindCoachmarkUi();
    _bindLifecycle();
    _log('start', 'ok', { version: ONBOARDING_VERSION });
    _showIntro();
}
